import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { OnlyMeToggle, usePrivatePages } from '../lib/privatePages';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { VenueOutreachScreen } from './VenueOutreachScreen';

/**
 * GROWTH HQ — Evan's growth back end (growth-agent handoff, 2026-10-12).
 *
 * One pipeline for venues AND adjacent companies AND people, on top of the existing outreach
 * machine: `outreach_targets` carries the new columns (target_kind/stage/assignee/priority/
 * pinned/next_step), and everything goes through the admin_growth_* definer RPCs because the
 * outreach tables deliberately have no RLS (see growth_hq_v1.sql).
 *
 * The full venue CRM + email machine + inbox is the existing VenueOutreachScreen, mounted whole
 * under the "Venues & email" tab — it moved here from the sidebar's Venue Portal slot.
 * Phone-usable on purpose: the pipeline renders as cards, not a wide table.
 */

const STAGES = ['discovered', 'contacted', 'meeting', 'agreed', 'live', 'active', 'churned'] as const;
type Stage = (typeof STAGES)[number];

type PipelineRow = {
  id: string;
  kind: 'venue' | 'company' | 'person';
  name: string;
  org_type: string | null;
  city: string | null;
  stage: Stage;
  status: string;
  assignee: string | null;
  priority: number;
  pinned: boolean;
  next_step: string | null;
  next_step_due: string | null;
  notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_instagram: string | null;
  venue_id: string | null;
  score: number | null;
  raters: number | null;
  sent_at: string | null;
  replied_at: string | null;
  updated_at: string;
};

type Kind = PipelineRow['kind'];
type VenueHit = { id: string; name: string; city: string | null; raters: number | null };
type LinkPair = { a: string; b: string };

const KIND_LABEL: Record<Kind, string> = { venue: 'venue', company: 'company', person: 'person' };
const KIND_CHIP: Record<Kind, string> = {
  venue: 'border-[#b79cff]/40 text-[#b79cff]',
  company: 'border-sky-500/40 text-sky-300',
  person: 'border-emerald-500/40 text-emerald-300',
};
const CONTACT_FILTERS_KEY = 'shado:growth:contactFilters';
/** Evan's home market, so it gets a one-tap filter (Evan 2026-11-04). */
const HOME_CITY = 'Ann Arbor';
const cityKey = (c: string | null | undefined) => (c ?? '').split(',')[0].trim().toLowerCase();

function readContactFilters(): { kind: 'all' | Kind; city: string } {
  try {
    const v = JSON.parse(window.localStorage.getItem(CONTACT_FILTERS_KEY) ?? '{}');
    return {
      kind: ['venue', 'company', 'person'].includes(v.kind) ? v.kind : 'all',
      city: typeof v.city === 'string' ? v.city : '',
    };
  } catch {
    return { kind: 'all', city: '' };
  }
}

type TeamMember = { id: number; name: string; city: string | null; focus: string | null; active: boolean };
type Activity = { id: number; target_id: string | null; actor: string; note: string; created_at: string };
type CityStat = {
  city: string;
  venues_live: number;
  tents: number;
  scans: number;
  signups: number;
  activated: number;
  rated_venues: number;
};

const STAGE_COLORS: Record<Stage, string> = {
  discovered: 'border-border text-mutedDim',
  contacted: 'border-sky-500/45 bg-sky-500/10 text-sky-300',
  meeting: 'border-amber-400/45 bg-amber-400/10 text-amber-300',
  agreed: 'border-accent/45 bg-accent/10 text-accent',
  live: 'border-emerald-500/45 bg-emerald-500/10 text-emerald-300',
  active: 'border-emerald-500/60 bg-emerald-500/20 text-emerald-200',
  churned: 'border-red-500/45 bg-red-500/10 text-red-300',
};

export function GrowthHQScreen() {
  const [tab, setTab] = useState<'portal' | 'contacts' | 'team' | 'cities'>('portal');
  const [rows, setRows] = useState<PipelineRow[] | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [cityStats, setCityStats] = useState<CityStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [kindFilter, setKindFilter] = useState<'all' | Kind>(() => readContactFilters().kind);
  const [cityFilter, setCityFilter] = useState(() => readContactFilters().city);
  const [searchQ, setSearchQ] = useState('');
  const [links, setLinks] = useState<LinkPair[]>([]);
  // False until growth_contact_links is applied: connection UI stays hidden rather than erroring.
  const [linksReady, setLinksReady] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [touchNote, setTouchNote] = useState('');
  // Team is editable only by the master admin (Evan) — enforced by RLS + is_master_admin(); this
  // just decides whether to draw the edit controls (Evan 2026-11-02).
  const { isMaster, isHidden, isPrivate } = usePrivatePages();

  const load = useCallback(async () => {
    setError(null);
    const [pipe, tm, act, lk] = await Promise.all([
      supabase.rpc('admin_growth_pipeline'),
      supabase.from('growth_team_members').select('*').order('name'),
      supabase.from('growth_activities').select('*').order('id', { ascending: false }).limit(200),
      supabase.rpc('admin_growth_links'),
    ]);
    if (pipe.error) {
      setError(
        /admin_growth_pipeline/.test(pipe.error.message)
          ? 'Growth HQ needs SQL applied: shado-admin/supabase/sql/growth_hq_v1.sql'
          : pipe.error.message,
      );
      setRows([]);
      return;
    }
    setRows((pipe.data ?? []) as PipelineRow[]);
    setTeam(((tm.data ?? []) as TeamMember[]).filter((m) => m.active));
    setActivities((act.data ?? []) as Activity[]);
    if (lk.error) {
      setLinksReady(false);
    } else {
      setLinksReady(true);
      setLinks((lk.data ?? []) as LinkPair[]);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(CONTACT_FILTERS_KEY, JSON.stringify({ kind: kindFilter, city: cityFilter }));
    } catch {
      /* private mode */
    }
  }, [kindFilter, cityFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'cities' || cityStats !== null) return;
    void supabase.rpc('admin_growth_city_stats').then(({ data, error: e }) => {
      if (e) setError(e.message);
      setCityStats((data ?? []) as CityStat[]);
    });
  }, [tab, cityStats]);

  const patch = useCallback(
    async (id: string, p: Record<string, unknown>) => {
      setRows((rs) =>
        (rs ?? []).map((r) =>
          r.id === id
            ? ({
                ...r,
                ...p,
                // These two columns surface under different names in the pipeline row.
                ...('display_name' in p ? { name: p.display_name } : {}),
                ...('city_label' in p ? { city: p.city_label } : {}),
              } as PipelineRow)
            : r,
        ),
      );
      const { error: e } = await supabase.rpc('admin_growth_update_target', { p_id: id, p_patch: p });
      if (e) {
        setError(e.message);
        void load();
      }
    },
    [load],
  );

  const logTouch = useCallback(
    async (targetId: string | null, note: string, actor = 'Evan') => {
      if (!note.trim()) return;
      const { error: e } = await supabase
        .from('growth_activities')
        .insert({ target_id: targetId, actor, note: note.trim() })
        .select('id');
      if (e) {
        setError(e.message);
        return;
      }
      setTouchNote('');
      const { data } = await supabase
        .from('growth_activities')
        .select('*')
        .order('id', { ascending: false })
        .limit(200);
      setActivities((data ?? []) as Activity[]);
    },
    [],
  );

  const byId = useMemo(() => new Map((rows ?? []).map((r) => [r.id, r])), [rows]);

  /** Everything connected to a contact, both directions. */
  const linkedTo = useMemo(() => {
    const m = new Map<string, PipelineRow[]>();
    const add = (from: string, to: string) => {
      const row = byId.get(to);
      if (!row) return;
      m.set(from, [...(m.get(from) ?? []), row]);
    };
    for (const l of links) {
      add(l.a, l.b);
      add(l.b, l.a);
    }
    return m;
  }, [links, byId]);

  const cityOptions = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    for (const r of rows ?? []) {
      const key = cityKey(r.city);
      if (!key) continue;
      const cur = m.get(key);
      m.set(key, { label: cur?.label ?? r.city!.split(',')[0].trim(), n: (cur?.n ?? 0) + 1 });
    }
    return Array.from(m.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    const list = (rows ?? []).filter((r) => {
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (cityFilter && cityKey(r.city) !== cityFilter) return false;
      if (q) {
        const hay = [
          r.name,
          r.contact_name,
          r.org_type,
          r.notes,
          r.next_step,
          ...(linkedTo.get(r.id) ?? []).map((l) => l.name),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (a.priority !== b.priority) return b.priority - a.priority;
      return (b.raters ?? -1) - (a.raters ?? -1);
    });
  }, [rows, kindFilter, cityFilter, searchQ, linkedTo]);

  /** The hyperlink: open that contact and bring it on screen, clearing filters only if they hide it. */
  const jumpTo = useCallback(
    (id: string) => {
      if (!filtered.some((r) => r.id === id)) {
        setKindFilter('all');
        setCityFilter('');
        setSearchQ('');
      }
      setOpenId(id);
      setTouchNote('');
      window.setTimeout(() => {
        document.getElementById(`contact-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
    },
    [filtered],
  );

  const connect = useCallback(
    async (a: string, b: string) => {
      setLinks((ls) => [...ls, { a, b }]);
      const { error: e } = await supabase.rpc('admin_growth_link', { p_a: a, p_b: b });
      if (e) setError(e.message);
      void load();
    },
    [load],
  );

  const disconnect = useCallback(
    async (a: string, b: string) => {
      setLinks((ls) => ls.filter((l) => !((l.a === a && l.b === b) || (l.a === b && l.b === a))));
      const { error: e } = await supabase.rpc('admin_growth_unlink', { p_a: a, p_b: b });
      if (e) setError(e.message);
      void load();
    },
    [load],
  );

  const weekAgo = Date.now() - 7 * 86400000;
  const touchesThisWeek = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of activities) {
      if (Date.parse(a.created_at) < weekAgo) continue;
      m.set(a.actor, (m.get(a.actor) ?? 0) + 1);
    }
    return m;
  }, [activities, weekAgo]);

  return (
    <div className="admin-page mx-auto max-w-none">
      <PageHeader
        title="Growth HQ"
        subtitle="Venue Portal = the venue CRM + email machine. Contacts = every venue, company and person, connected to each other."
      />

      <div className="mb-5 flex items-end gap-0.5 overflow-x-auto border-b border-borderStrong">
        {(
          [
            ['portal', 'Venue Portal'],
            ['contacts', 'Contacts'],
            ['team', 'Team'],
            ['cities', 'Cities'],
          ] as const
        )
          // A tab the master admin made private vanishes for everyone else (Evan 2026-11-02).
          .filter(([id]) => !isHidden(`/growth#${id}`))
          .map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 pb-2.5 pt-1 text-[12.5px] font-medium transition-colors ${
              tab === id ? 'border-accent text-white' : 'border-transparent text-mutedDim hover:text-white'
            }`}
          >
            {label}
            {isMaster && isPrivate(`/growth#${id}`) ? <span className="ml-1 text-[9px] text-accent">●</span> : null}
          </button>
        ))}
        <div className="ml-auto pb-2">
          <OnlyMeToggle pageKey={`/growth#${tab}`} />
        </div>
      </div>

      {error ? <div className="admin-banner-error mb-4">{error}</div> : null}

      {tab === 'portal' ? <VenueOutreachScreen /> : null}

      {tab === 'contacts' ? (
        <div className="mx-auto max-w-4xl">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {(
              [
                ['all', 'All'],
                ['venue', 'Venues'],
                ['company', 'Companies'],
                ['person', 'People'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-active={kindFilter === k}
                onClick={() => setKindFilter(k)}
                className="admin-pill"
              >
                {label}
              </button>
            ))}
            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
            <button
              type="button"
              data-active={cityFilter === cityKey(HOME_CITY)}
              onClick={() => setCityFilter((c) => (c === cityKey(HOME_CITY) ? '' : cityKey(HOME_CITY)))}
              className="admin-pill"
            >
              {HOME_CITY}
            </button>
            <select
              className="admin-input !w-auto !px-2 !text-[12px]"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              aria-label="Filter by city"
            >
              <option value="">All cities</option>
              {cityOptions.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label} ({c.n})
                </option>
              ))}
            </select>
            <input
              className="admin-input !w-[170px] !text-[12px]"
              placeholder="search…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setAddOpen((o) => !o)}
              className="admin-btn admin-btn-secondary admin-btn-sm ml-auto"
            >
              {addOpen ? 'Close' : '+ Add'}
            </button>
          </div>

          {addOpen ? (
            <AddTarget
              team={team}
              rows={rows ?? []}
              linksReady={linksReady}
              onAdded={(id) => {
                setAddOpen(false);
                void load().then(() => jumpTo(id));
              }}
            />
          ) : null}

          {rows === null ? (
            <SkeletonRows rows={8} />
          ) : filtered.length === 0 ? (
            <EmptyState title="Nothing matches" hint="Change the filters, or add a venue, company or person with + Add." />
          ) : (
            <div className="space-y-2">
              {filtered.map((r) => {
                const open = openId === r.id;
                return (
                  <div key={r.id} id={`contact-${r.id}`} className="admin-card !p-0">
                    <button type="button" onClick={() => setOpenId(open ? null : r.id)} className="block w-full px-4 py-2.5 text-left">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void patch(r.id, { pinned: !r.pinned });
                          }}
                          title={r.pinned ? 'Unpin' : 'Pin to top'}
                          className={`shrink-0 text-[15px] ${r.pinned ? 'text-amber-300' : 'text-mutedDim hover:text-amber-300'}`}
                        >
                          {r.pinned ? '★' : '☆'}
                        </button>
                        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-white">{r.name}</span>
                        <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAGE_COLORS[r.stage] ?? STAGE_COLORS.discovered}`}>
                          {r.stage}
                        </span>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${KIND_CHIP[r.kind]}`}>
                          {r.org_type || KIND_LABEL[r.kind]}
                        </span>
                        <span className="shrink-0 text-[11px] text-mutedDim">{r.city ?? '—'}</span>
                        {r.kind === 'venue' ? (
                          <span className="shrink-0 font-display text-[12.5px] font-semibold tabular-nums text-[#b79cff]">
                            {r.score != null ? Number(r.score).toFixed(1) : '—'}
                            <span className="ml-1 font-sans text-[10px] font-normal text-mutedDim">{r.raters ?? 0} raters</span>
                          </span>
                        ) : null}
                        {r.assignee ? <span className="shrink-0 text-[10.5px] text-sky-300">{r.assignee}</span> : null}
                        {r.contact_email ? <span className="shrink-0 text-[10.5px] text-emerald-300">✉</span> : null}
                        {r.contact_phone ? <span className="shrink-0 text-[10.5px] text-emerald-300">☎</span> : null}
                        {r.contact_instagram ? <span className="shrink-0 text-[10.5px] text-emerald-300">IG</span> : null}
                      </div>
                      {r.next_step ? (
                        <div className="mt-1 pl-7 text-[11.5px] text-muted">
                          → {r.next_step}
                          {r.next_step_due ? <span className="text-mutedDim"> · by {r.next_step_due}</span> : null}
                        </div>
                      ) : null}
                    </button>

                    {!open && (linkedTo.get(r.id) ?? []).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 px-4 pb-2.5 pl-11">
                        {(linkedTo.get(r.id) ?? []).map((l) => (
                          <ContactChip key={l.id} row={l} onOpen={() => jumpTo(l.id)} />
                        ))}
                      </div>
                    ) : null}

                    {open ? (
                      <div className="space-y-3 border-t border-border/60 px-4 py-3">
                        {linksReady ? (
                        <div>
                          <div className="admin-eyebrow mb-1.5">Connected to</div>
                          {(linkedTo.get(r.id) ?? []).length > 0 ? (
                            <div className="mb-2 flex flex-wrap gap-1.5">
                              {(linkedTo.get(r.id) ?? []).map((l) => (
                                <ContactChip
                                  key={l.id}
                                  row={l}
                                  onOpen={() => jumpTo(l.id)}
                                  onRemove={() => void disconnect(r.id, l.id)}
                                />
                              ))}
                            </div>
                          ) : null}
                          <ContactPicker
                            rows={rows ?? []}
                            excludeIds={[r.id, ...(linkedTo.get(r.id) ?? []).map((l) => l.id)]}
                            placeholder="search venues, companies, people…"
                            onPick={(p) => {
                              void resolvePick(p, r.city, r.assignee).then(
                                (id) => connect(r.id, id),
                                (e: Error) => setError(e.message),
                              );
                            }}
                          />
                        </div>
                        ) : null}
                        {r.kind !== 'venue' ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              className="admin-input flex-1 !text-[12px]"
                              placeholder="name"
                              defaultValue={r.name}
                              onBlur={(e) => {
                                if (e.target.value.trim() && e.target.value !== r.name) void patch(r.id, { display_name: e.target.value.trim() });
                              }}
                            />
                            <input
                              className="admin-input !w-[150px] !text-[12px]"
                              placeholder="type (formals, GM…)"
                              defaultValue={r.org_type ?? ''}
                              onBlur={(e) => {
                                if (e.target.value !== (r.org_type ?? '')) void patch(r.id, { org_type: e.target.value });
                              }}
                            />
                            <input
                              className="admin-input !w-[130px] !text-[12px]"
                              placeholder="city"
                              defaultValue={r.city ?? ''}
                              onBlur={(e) => {
                                if (e.target.value !== (r.city ?? '')) void patch(r.id, { city_label: e.target.value });
                              }}
                            />
                          </div>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            className="admin-input !w-auto !px-2 !text-[12px]"
                            value={r.stage}
                            onChange={(e) => void patch(r.id, { stage: e.target.value })}
                          >
                            {STAGES.map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                          <select
                            className="admin-input !w-auto !px-2 !text-[12px]"
                            value={r.assignee ?? ''}
                            onChange={(e) => void patch(r.id, { assignee: e.target.value })}
                          >
                            <option value="">unassigned</option>
                            {team.map((m) => (
                              <option key={m.id} value={m.name}>{m.name}</option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1 text-[11px] text-mutedDim">
                            priority
                            <input
                              type="number"
                              className="admin-input !w-[64px] !px-1.5 !text-[12px]"
                              value={r.priority}
                              onChange={(e) => void patch(r.id, { priority: Math.round(Number(e.target.value) || 0) })}
                            />
                          </label>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="next step…"
                            defaultValue={r.next_step ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.next_step ?? '')) void patch(r.id, { next_step: e.target.value });
                            }}
                          />
                          <input
                            type="date"
                            className="admin-input !w-auto !text-[12px] [color-scheme:dark]"
                            defaultValue={r.next_step_due ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.next_step_due ?? '')) void patch(r.id, { next_step_due: e.target.value });
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="contact name"
                            defaultValue={r.contact_name ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_name ?? '')) void patch(r.id, { contact_name: e.target.value });
                            }}
                          />
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="contact email"
                            defaultValue={r.contact_email ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_email ?? '')) void patch(r.id, { contact_email: e.target.value });
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="phone"
                            defaultValue={r.contact_phone ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_phone ?? '')) void patch(r.id, { contact_phone: e.target.value });
                            }}
                          />
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="instagram @handle"
                            defaultValue={r.contact_instagram ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_instagram ?? '')) void patch(r.id, { contact_instagram: e.target.value });
                            }}
                          />
                        </div>
                        <textarea
                          rows={2}
                          className="admin-input !h-auto w-full resize-y !py-2 !text-[12px]"
                          placeholder="notes…"
                          defaultValue={r.notes ?? ''}
                          onBlur={(e) => {
                            if (e.target.value !== (r.notes ?? '')) void patch(r.id, { notes: e.target.value });
                          }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="log a touch — call, DM, walk-in…"
                            value={openId === r.id ? touchNote : ''}
                            onChange={(e) => setTouchNote(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void logTouch(r.id, touchNote);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => void logTouch(r.id, touchNote)}
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                          >
                            Log
                          </button>
                        </div>
                        <div className="space-y-1">
                          {activities
                            .filter((a) => a.target_id === r.id)
                            .slice(0, 6)
                            .map((a) => (
                              <div key={a.id} className="text-[11px] text-mutedDim">
                                <span className="text-muted">{a.actor}</span> · {a.note} ·{' '}
                                {timeAgo(a.created_at)}
                              </div>
                            ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {tab === 'cities' ? (
        <div className="mx-auto max-w-4xl">
          {cityStats === null ? (
            <SkeletonRows rows={6} />
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th>City</th>
                    <th className="num">Venues live</th>
                    <th className="num">Tents</th>
                    <th className="num">Scans</th>
                    <th className="num">Signups</th>
                    <th className="num">Activated</th>
                    <th className="num">Rated venues</th>
                  </tr>
                </thead>
                <tbody>
                  {cityStats.map((c) => (
                    <tr key={c.city}>
                      <td className="key">{c.city}</td>
                      <td className="num">{c.venues_live}</td>
                      <td className="num">{c.tents}</td>
                      <td className="num">{c.scans}</td>
                      <td className="num">{c.signups}</td>
                      <td className="num">{c.activated}</td>
                      <td className="num">{c.rated_venues}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-mutedDim">
                Venues live = active approved offers. Signups/activated match on the profile&rsquo;s home
                city, so they undercount students whose hometown differs — tent-scan attribution is the
                real fix and counts here the moment scans happen.
              </p>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'team' && !isHidden('/growth#team') ? (
        <div className="mx-auto max-w-3xl space-y-2">
          {team.map((m) =>
            isMaster ? (
              <TeamRowEditor
                key={m.id}
                m={m}
                touches={touchesThisWeek.get(m.name) ?? 0}
                onSaved={() => void load()}
                onError={setError}
              />
            ) : (
              <div key={m.id} className="admin-card flex flex-wrap items-center gap-3 !py-3">
                <span className="text-[13.5px] font-medium text-white">{m.name}</span>
                <span className="text-[11px] text-mutedDim">{m.city ?? '—'}</span>
                <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted">{m.focus ?? ''}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-sky-300">
                  {touchesThisWeek.get(m.name) ?? 0} touches this week
                </span>
              </div>
            ),
          )}
          {isMaster ? <AddTeamMember onAdded={() => void load()} onError={setError} /> : null}
          <div className="admin-card">
            <div className="admin-eyebrow mb-2">This week</div>
            {activities.filter((a) => Date.parse(a.created_at) >= weekAgo).length === 0 ? (
              <p className="text-[11.5px] text-mutedDim">No touches logged yet — log them from any pipeline row.</p>
            ) : (
              <div className="space-y-1">
                {activities
                  .filter((a) => Date.parse(a.created_at) >= weekAgo)
                  .slice(0, 30)
                  .map((a) => (
                    <div key={a.id} className="text-[11.5px] text-muted">
                      <span className="text-white">{a.actor}</span> · {a.note}{' '}
                      <span className="text-mutedDim">· {timeAgo(a.created_at)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** What a picker hands back: an existing contact, a venue not in Contacts yet, or a new name. */
type Pick =
  | { type: 'target'; id: string; label: string; city: string | null }
  | { type: 'venue'; venue: VenueHit }
  | { type: 'new'; kind: 'company' | 'person'; name: string };

/** Turns a pick into a contact id, creating the venue / company / person row if needed. */
async function resolvePick(p: Pick, city: string | null, assignee: string | null): Promise<string> {
  if (p.type === 'target') return p.id;
  if (p.type === 'venue') {
    const { data, error } = await supabase.rpc('admin_growth_add_venue', {
      p_venue_id: p.venue.id,
      p_assignee: assignee,
    });
    if (error) throw new Error(error.message);
    return data as string;
  }
  const { data, error } = await supabase.rpc('admin_growth_add_target', {
    p_kind: p.kind,
    p_name: p.name,
    p_city: city,
    p_assignee: assignee,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/** A connected contact: click the name to go there, × to disconnect. */
function ContactChip({ row, onOpen, onRemove }: { row: PipelineRow; onOpen: () => void; onRemove?: () => void }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${KIND_CHIP[row.kind]}`}>
      <button type="button" onClick={onOpen} className="underline-offset-2 hover:underline">
        {row.name}
      </button>
      {onRemove ? (
        <button type="button" onClick={onRemove} aria-label={`Disconnect ${row.name}`} className="opacity-60 hover:opacity-100">
          ×
        </button>
      ) : null}
    </span>
  );
}

/** Search box over existing contacts + every venue on Shado, with "create new" at the bottom. */
function ContactPicker({
  rows,
  excludeIds,
  placeholder,
  venuesOnly,
  onPick,
}: {
  rows: PipelineRow[];
  excludeIds: string[];
  placeholder: string;
  venuesOnly?: boolean;
  onPick: (p: Pick) => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<VenueHit[]>([]);
  const query = q.trim();

  useEffect(() => {
    if (query.length < 2) {
      setHits([]);
      return;
    }
    let stale = false;
    const t = window.setTimeout(() => {
      void supabase.rpc('admin_growth_search_venues', { p_query: query }).then(({ data }) => {
        if (!stale) setHits((data ?? []) as VenueHit[]);
      });
    }, 220);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [query]);

  const lower = query.toLowerCase();
  const matches =
    lower.length < 2
      ? []
      : rows
          .filter((r) => !excludeIds.includes(r.id))
          .filter((r) => !venuesOnly || r.kind === 'venue')
          .filter((r) => r.name.toLowerCase().includes(lower) || (r.contact_name ?? '').toLowerCase().includes(lower))
          .slice(0, 8);
  const knownVenueIds = new Set(rows.map((r) => r.venue_id).filter(Boolean));
  const venueHits = hits.filter((h) => !knownVenueIds.has(h.id));
  const pick = (p: Pick) => {
    setQ('');
    setHits([]);
    onPick(p);
  };
  const rowClass = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-white hover:bg-white/[0.05]';

  return (
    <div>
      <input
        className="admin-input w-full !text-[12px]"
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {query.length >= 2 ? (
        <div className="mt-1 max-h-[240px] overflow-y-auto rounded-lg border border-border bg-surface">
          {matches.map((r) => (
            <button
              key={r.id}
              type="button"
              className={rowClass}
              onClick={() => pick({ type: 'target', id: r.id, label: r.name, city: r.city })}
            >
              <span className={`rounded-full border px-1.5 text-[10px] ${KIND_CHIP[r.kind]}`}>{KIND_LABEL[r.kind]}</span>
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              <span className="text-[11px] text-mutedDim">{r.city ?? ''}</span>
            </button>
          ))}
          {venueHits.map((h) => (
            <button key={h.id} type="button" className={rowClass} onClick={() => pick({ type: 'venue', venue: h })}>
              <span className={`rounded-full border px-1.5 text-[10px] ${KIND_CHIP.venue}`}>venue</span>
              <span className="min-w-0 flex-1 truncate">{h.name}</span>
              <span className="text-[11px] text-mutedDim">{h.city ?? ''}</span>
            </button>
          ))}
          {!venuesOnly
            ? (['person', 'company'] as const).map((k) => (
                <button key={k} type="button" className={`${rowClass} text-muted`} onClick={() => pick({ type: 'new', kind: k, name: query })}>
                  + New {k} &ldquo;{query}&rdquo;
                </button>
              ))
            : null}
          {venuesOnly && matches.length === 0 && venueHits.length === 0 ? (
            <div className="px-3 py-2 text-[11.5px] text-mutedDim">No venue by that name.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function AddTarget({
  team,
  rows,
  linksReady,
  onAdded,
}: {
  team: TeamMember[];
  rows: PipelineRow[];
  linksReady: boolean;
  onAdded: (id: string) => void;
}) {
  const [kind, setKind] = useState<Kind>('person');
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [city, setCity] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactInstagram, setContactInstagram] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [assignee, setAssignee] = useState('Evan');
  const [connections, setConnections] = useState<Pick[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pickLabel = (p: Pick) => (p.type === 'target' ? p.label : p.type === 'venue' ? p.venue.name : p.name);
  const pickCity = (p: Pick) => (p.type === 'target' ? p.city : p.type === 'venue' ? p.venue.city : null);

  const addVenue = async (p: Pick) => {
    setBusy(true);
    setErr(null);
    try {
      onAdded(await resolvePick(p, null, assignee || null));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      // A person met at an Ann Arbor venue should show under the Ann Arbor filter without retyping it.
      const cityValue = city.trim() || connections.map(pickCity).find(Boolean) || null;
      const { data, error } = await supabase.rpc('admin_growth_add_target', {
        p_kind: kind,
        p_name: name.trim(),
        p_org_type: orgType.trim() || null,
        p_city: cityValue,
        p_contact_name: contactName.trim() || null,
        p_contact_email: contactEmail.trim() || null,
        p_next_step: nextStep.trim() || null,
        p_assignee: assignee || null,
        p_contact_phone: contactPhone.trim() || null,
        p_contact_instagram: contactInstagram.trim() || null,
      });
      if (error) throw new Error(error.message);
      const id = data as string;
      for (const p of connections) {
        const other = await resolvePick(p, cityValue, assignee || null);
        const { error: e } = await supabase.rpc('admin_growth_link', { p_a: id, p_b: other });
        if (e) throw new Error(e.message);
      }
      onAdded(id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-card mb-4 space-y-2">
      {err ? <div className="admin-banner-error">{err}</div> : null}
      <div className="flex flex-wrap items-center gap-2">
        {(linksReady ? (['venue', 'company', 'person'] as const) : (['company', 'person'] as const)).map((k) => (
          <button key={k} type="button" data-active={kind === k} onClick={() => setKind(k)} className="admin-pill">
            {k}
          </button>
        ))}
        {kind !== 'venue' ? (
          <>
            <input className="admin-input flex-1 !text-[12px]" placeholder={kind === 'company' ? 'company name' : "person's name"} value={name} onChange={(e) => setName(e.target.value)} />
            <input className="admin-input !w-[150px] !text-[12px]" placeholder={kind === 'company' ? 'type (formals, brand…)' : 'role (GM, owner…)'} value={orgType} onChange={(e) => setOrgType(e.target.value)} />
            <input className="admin-input !w-[130px] !text-[12px]" placeholder="city" value={city} onChange={(e) => setCity(e.target.value)} />
          </>
        ) : null}
      </div>
      {kind === 'venue' ? (
        <ContactPicker
          rows={rows}
          excludeIds={[]}
          venuesOnly
          placeholder="search any venue on Shado…"
          onPick={(p) => void addVenue(p)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {kind === 'company' ? (
              <input className="admin-input flex-1 !text-[12px]" placeholder="contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            ) : null}
            <input className="admin-input flex-1 !text-[12px]" placeholder="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            <input className="admin-input !w-[140px] !text-[12px]" placeholder="phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            <input className="admin-input !w-[150px] !text-[12px]" placeholder="instagram" value={contactInstagram} onChange={(e) => setContactInstagram(e.target.value)} />
            <select className="admin-input !w-auto !px-2 !text-[12px]" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              {team.map((m) => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
            </select>
          </div>
          {linksReady ? (
          <div>
            {connections.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {connections.map((p, i) => (
                  <span key={`${pickLabel(p)}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-white">
                    {pickLabel(p)}
                    <button
                      type="button"
                      aria-label={`Remove ${pickLabel(p)}`}
                      className="opacity-60 hover:opacity-100"
                      onClick={() => setConnections((c) => c.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <ContactPicker
              rows={rows}
              excludeIds={connections.flatMap((p) => (p.type === 'target' ? [p.id] : []))}
              placeholder="connect to a venue, company or person…"
              onPick={(p) => setConnections((c) => [...c, p])}
            />
          </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <input className="admin-input flex-1 !text-[12px]" placeholder="next step" value={nextStep} onChange={(e) => setNextStep(e.target.value)} />
            <button
              type="button"
              disabled={busy || !name.trim()}
              onClick={() => void submit()}
              className="admin-btn admin-btn-primary admin-btn-sm"
            >
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Inline-editable team row (master admin only). Name goes through master_rename_team_member so
 *  logged touches and pipeline assignments follow the rename; city/focus are plain updates. */
function TeamRowEditor({
  m,
  touches,
  onSaved,
  onError,
}: {
  m: TeamMember;
  touches: number;
  onSaved: () => void;
  onError: (e: string | null) => void;
}) {
  const [name, setName] = useState(m.name);
  const [city, setCity] = useState(m.city ?? '');
  const [focus, setFocus] = useState(m.focus ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(m.name);
    setCity(m.city ?? '');
    setFocus(m.focus ?? '');
  }, [m.name, m.city, m.focus]);
  const dirty = name.trim() !== m.name || city.trim() !== (m.city ?? '') || focus.trim() !== (m.focus ?? '');

  const save = async () => {
    if (!name.trim()) return onError('Name is required');
    setBusy(true);
    onError(null);
    if (name.trim() !== m.name) {
      const { error } = await supabase.rpc('master_rename_team_member', { p_id: m.id, p_name: name.trim() });
      if (error) {
        setBusy(false);
        return onError(/duplicate key/.test(error.message) ? `There's already a team member named ${name.trim()}` : error.message);
      }
    }
    const { error } = await supabase
      .from('growth_team_members')
      .update({ city: city.trim() || null, focus: focus.trim() || null })
      .eq('id', m.id)
      .select('id');
    setBusy(false);
    if (error) return onError(error.message);
    onSaved();
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${m.name} from the team? Their logged touches stay in the history.`)) return;
    setBusy(true);
    const { error } = await supabase.from('growth_team_members').update({ active: false }).eq('id', m.id).select('id');
    setBusy(false);
    if (error) return onError(error.message);
    onSaved();
  };

  const field = 'admin-input !py-1.5 !text-[12.5px]';
  return (
    <div className="admin-card flex flex-wrap items-center gap-2 !py-3">
      <input className={`${field} !w-[130px] font-medium`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
      <input className={`${field} !w-[130px]`} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
      <input className={`${field} min-w-[180px] flex-1`} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus" />
      <span className="shrink-0 text-[11px] tabular-nums text-sky-300">{touches} touches this week</span>
      {dirty ? (
        <button type="button" disabled={busy} onClick={() => void save()} className="rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-black disabled:opacity-50">
          Save
        </button>
      ) : null}
      <button type="button" disabled={busy} onClick={() => void remove()} className="text-[11.5px] text-red-400 hover:underline disabled:opacity-50">
        Remove
      </button>
    </div>
  );
}

function AddTeamMember({ onAdded, onError }: { onAdded: () => void; onError: (e: string | null) => void }) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const n = name.trim();
    if (!n) return onError('Name is required');
    setBusy(true);
    onError(null);
    // A previously removed member with the same name comes back instead of colliding.
    const { data: existing } = await supabase.from('growth_team_members').select('id').eq('name', n).maybeSingle();
    const { error } = existing
      ? await supabase
          .from('growth_team_members')
          .update({ active: true, city: city.trim() || null, focus: focus.trim() || null })
          .eq('id', (existing as { id: number }).id)
      : await supabase.from('growth_team_members').insert({ name: n, city: city.trim() || null, focus: focus.trim() || null });
    setBusy(false);
    if (error) return onError(error.message);
    setName('');
    setCity('');
    setFocus('');
    onAdded();
  };
  const field = 'admin-input !py-1.5 !text-[12.5px]';
  return (
    <div className="admin-card flex flex-wrap items-center gap-2 !py-3">
      <input className={`${field} !w-[130px]`} value={name} onChange={(e) => setName(e.target.value)} placeholder="New member" />
      <input className={`${field} !w-[130px]`} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
      <input className={`${field} min-w-[180px] flex-1`} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus" />
      <button type="button" disabled={busy || !name.trim()} onClick={() => void add()} className="rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-black disabled:opacity-50">
        Add
      </button>
    </div>
  );
}
